import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TimeEntryApprovalStatus } from "@torre/work-hours";
import { formatMinutes } from "@torre/work-hours";
import { useTheme } from "../../../context/ThemeContext";
import {
  approveTimeEntry,
  fetchApprovals,
  rejectTimeEntry,
  timeEntryErrorMessage,
  type ApprovalRow,
  type ApprovalsResponse,
} from "../../work-hours/services/workHoursService";
import { buildThemeClasses, formatAmount } from "../theme";
import ActivityLink from "../../work-hours/components/ActivityLink";
import { MONEY_PRIVACY_CLASS } from "../../../utils/moneyPrivacy";

// ---------------------------------------------------------------------------
// Horas por aprobar (#1281) — el tablero del tech lead dentro de Nómina.
//
// Las entradas de tiempo MANUALES (consola o MCP) de más de 1 h nacen pendientes
// y no cuentan como horas reales ni entran a la asistencia hasta que alguien con
// autoridad las apruebe: los dueños, un colaborador con rol tech lead o el líder
// del squad de la persona. Nadie aprueba lo suyo. Rechazar exige motivo.
//
// La tabla muestra el detalle de cada hora: de quién es, qué hizo, en qué
// proyecto y repo, cuántas horas, cuándo la registró y —SOLO para los dueños,
// porque lleva sueldos— cuánto equivale según su pago (costo por hora × horas).
// Quien no es dueño no recibe los importes: la API no se los manda.
// El importe sale en pesos Y en dólares: el de la moneda de pago de la persona
// es el dato; el otro es su equivalente (≈) con el tipo de cambio de la plataforma.
//
// Esta pantalla PINTA; la autoridad la decide el servidor en cada petición.
//
// #1346: vive en su propia sección de Nómina (`/payroll/hours-approval`), ya no
// encima de las incidencias. Cada solicitud se lee completa —quién, día
// trabajado, proyecto/repo, tipo, tiempo, nota, cuándo se pidió y estado— y la
// regla es pareja: dueños y tech leads también esperan aprobación en lo suyo, y
// su propia fila llega sin botones. Quien no aprueba a nadie ve SUS solicitudes
// y su estado (`meta.scope === "own"`): antes no tenía dónde enterarse de un
// rechazo.
// ---------------------------------------------------------------------------

const STATUSES: TimeEntryApprovalStatus[] = ["pending", "approved", "rejected"];

const STATUS_TONES: Record<TimeEntryApprovalStatus, string> = {
  pending: "bg-amber-500/15 text-amber-600",
  approved: "bg-emerald-500/15 text-emerald-600",
  rejected: "bg-red-500/15 text-red-500",
};

/** Tono de la etiqueta de origen: de dónde llegó la solicitud. */
const ORIGIN_TONES: Record<string, string> = {
  skill: "bg-violet-500/15 text-violet-600",
  ui: "bg-sky-500/15 text-sky-600",
  mcp: "bg-teal-500/15 text-teal-600",
  admin: "bg-gray-400/15 text-gray-500",
  other: "bg-gray-400/15 text-gray-500",
};

/** Las dos monedas en que se muestra cada importe. */
const AMOUNT_CURRENCIES = ["MXN", "USD"] as const;
type AmountCurrency = (typeof AMOUNT_CURRENCIES)[number];

/**
 * Importe de una fila en `currency`: el propio si es su moneda de pago; si no,
 * el equivalente que manda la API. `null` = no hay tipo de cambio para darlo.
 */
export const amountIn = (amount: ApprovalRow["amount"], currency: AmountCurrency): { value: number; converted: boolean } | null => {
  if (!amount) return null;
  if (amount.currency === currency) return { value: amount.value, converted: false };
  const value = currency === "MXN" ? amount.mxn : amount.usd;
  return typeof value === "number" && Number.isFinite(value) ? { value, converted: true } : null;
};

/** Cuánta evidencia cita una entrada (commits, PRs, issues, artefactos). */
export const evidenceCount = (evidence: { commits?: string[]; prs?: string[]; issues?: string[]; artifacts?: string[] } | null | undefined) => {
  const n = (list: string[] | undefined) => (Array.isArray(list) ? list.filter(Boolean).length : 0);
  const counts = { commits: n(evidence?.commits), prs: n(evidence?.prs), issues: n(evidence?.issues), artifacts: n(evidence?.artifacts) };
  return { ...counts, total: counts.commits + counts.prs + counts.issues + counts.artifacts };
};

interface Props {
  /** Se llama tras aprobar o rechazar, para que la pantalla madre refresque sus conteos. */
  onDecided?: () => void;
  /** Cambia para recargar desde fuera (p. ej. tras registrar horas en la página). */
  refreshKey?: unknown;
}

export default function HoursApprovalPanel({ onDecided, refreshKey }: Props) {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const language = i18n?.language || "es";

  const [status, setStatus] = useState<TimeEntryApprovalStatus>("pending");
  const [data, setData] = useState<ApprovalsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Cada carga lleva su número (#1346): al cambiar de pestaña mientras otra
  // respuesta viaja, la vieja se descarta en vez de pintar, p. ej., las
  // rechazadas bajo «Pendientes».
  const requestId = useRef(0);
  const shown = useRef<TimeEntryApprovalStatus | null>(null);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    // Las filas de OTRA pestaña no se quedan mientras llega la nueva.
    if (shown.current !== status) setData(null);
    setLoading(true);
    setError(null);
    try {
      const next = await fetchApprovals({ status });
      if (id !== requestId.current) return;
      shown.current = status;
      setData(next);
    } catch {
      if (id !== requestId.current) return;
      setError(t("payroll.hoursApproval.loadError"));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [status, t]);

  useEffect(
    () => () => {
      requestId.current += 1;
    },
    [],
  );

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const decide = async (row: ApprovalRow, next: "approved" | "rejected") => {
    setActionError(null);
    let reason = "";
    if (next === "rejected") {
      // Sin motivo la persona no sabe qué corregir; el servidor también lo exige.
      reason = (window.prompt(t("payroll.hoursApproval.rejectPrompt") ?? "") ?? "").trim();
      if (reason.length < 5) return;
    }
    setBusy(row.id);
    try {
      if (next === "approved") await approveTimeEntry(row.id);
      else await rejectTimeEntry(row.id, reason);
      await load();
      onDecided?.();
    } catch (err) {
      setActionError(timeEntryErrorMessage(err) ?? t("payroll.hoursApproval.actionError"));
    } finally {
      setBusy(null);
    }
  };

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const canSeeAmounts = Boolean(meta?.canSeeAmounts);
  // #1346: quien no aprueba a nadie recibe solo lo suyo; no hay acciones que pintar.
  const ownOnly = (meta?.scope as string | undefined) === "own";
  // #1346: pendientes que nadie más puede decidir (un solo dueño y sin tech lead).
  // No se aprueban solas: se avisa para que alguien dé de alta a otro aprobador.
  const withoutApprover = Number((meta as { withoutApprover?: number } | undefined)?.withoutApprover) || 0;
  const lacksApprover = (r: ApprovalRow) => (r as ApprovalRow & { hasApprover?: boolean }).hasApprover === false;
  const amountTotal = useMemo(() => {
    const by = meta?.amountByCurrency ?? {};
    return Object.keys(by)
      .sort()
      .map((c) => formatAmount(by[c], c))
      .join(" · ");
  }, [meta?.amountByCurrency]);
  // Total equivalente en pesos y en dólares; «—» en la moneda sin tipo de cambio.
  const totals = meta?.amountTotals;
  const fxUsed = Object.entries(meta?.fx ?? {}).sort(([a], [b]) => a.localeCompare(b));
  const fxMissing = meta?.fxMissing ?? [];

  const dateLabel = (iso: string | null | undefined) => (iso ? String(iso).slice(0, 10) : "—");
  const sourceLabel = (s: string) => t(`payroll.hoursApproval.sources.${s}`, { defaultValue: s });

  return (
    <section className={`mb-6 rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`} data-testid="hours-approval">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className={`flex items-center gap-2 text-base font-semibold ${tc.text}`}>
            <i className="ri-hourglass-line text-amber-500" />
            {t("payroll.hoursApproval.title")}
            {meta && status === "pending" && (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-600" data-testid="hours-approval-count">
                {meta.total}
              </span>
            )}
          </h2>
          <p className={`mt-0.5 text-xs ${tc.textSecondary}`}>{t(ownOnly ? "payroll.hoursApprovalPage.subtitleOwn" : "payroll.hoursApproval.subtitle")}</p>
          <p className={`mt-0.5 text-[11px] ${tc.textSecondary}`}>{t("payroll.hoursApproval.rule")}</p>
        </div>
        <div className="flex gap-1" role="tablist" aria-label={t("payroll.hoursApproval.title")}>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={status === s}
              onClick={() => setStatus(s)}
              data-testid={`hours-approval-tab-${s}`}
              className={`rounded-lg border px-2.5 py-1 text-xs ${status === s ? "border-violet-500 bg-violet-500/10 text-violet-500" : `${tc.cardBorder} ${tc.textSecondary}`}`}
            >
              {t(`payroll.hoursApproval.status.${s}`)}
            </button>
          ))}
        </div>
      </div>

      {meta && (
        <div className={`mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${tc.textSecondary}`} data-testid="hours-approval-totals">
          <span className={`font-medium ${tc.text}`}>{t("payroll.hoursApproval.totals", { count: meta.total, hours: meta.totalHours })}</span>
          {canSeeAmounts && totals && (
            <span className={`font-medium ${tc.text} ${MONEY_PRIVACY_CLASS}`} data-testid="hours-approval-amount-totals">
              {t("payroll.hoursApproval.amountTotals", { mxn: formatAmount(totals.mxn, "MXN"), usd: formatAmount(totals.usd, "USD") })}
            </span>
          )}
          {canSeeAmounts && amountTotal && (
            <span className={MONEY_PRIVACY_CLASS} data-testid="hours-approval-amount-total">
              {t(totals ? "payroll.hoursApproval.amountPaidIn" : "payroll.hoursApproval.amountTotal", { amount: amountTotal })}
            </span>
          )}
          {canSeeAmounts &&
            fxUsed.map(([currency, fx]) => (
              <span key={currency} data-testid={`hours-approval-fx-${currency}`}>
                {t("payroll.hoursApproval.fxNote", { currency, rate: fx.rate.toFixed(4), date: fx.date ?? "—", source: fx.source ?? "—" })}
              </span>
            ))}
          {canSeeAmounts && fxMissing.length > 0 && (
            <span className="font-medium text-amber-600" data-testid="hours-approval-fx-missing">
              {t("payroll.hoursApproval.fxMissing", { currencies: fxMissing.join(", ") })}
            </span>
          )}
          {canSeeAmounts && (meta.unpriced ?? 0) > 0 && <span>{t("payroll.hoursApproval.unpriced", { count: meta.unpriced })}</span>}
          {!canSeeAmounts && !ownOnly && <span data-testid="hours-approval-owners-only">{t("payroll.hoursApproval.ownersOnlyAmounts")}</span>}
          {ownOnly && <span data-testid="hours-approval-scope-own">{t("payroll.hoursApprovalPage.scopeOwn")}</span>}
          {meta.scope === "squads" && <span data-testid="hours-approval-scope">{t("payroll.hoursApproval.scopeSquads")}</span>}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500">
          <i className="ri-error-warning-line mr-1" />
          {error}
        </div>
      )}
      {actionError && (
        <div className="mb-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500" data-testid="hours-approval-action-error">
          <i className="ri-error-warning-line mr-1" />
          {actionError}
        </div>
      )}

      {status === "pending" && withoutApprover > 0 && (
        <div className="mb-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-600" data-testid="hours-approval-no-approver-banner">
          <i className="ri-user-unfollow-line mr-1" />
          {t("payroll.hoursApprovalPage.noApproverBanner", { count: withoutApprover })}
        </div>
      )}

      {loading && !data && <p className={`text-sm ${tc.textSecondary}`}>…</p>}
      {!error && data && rows.length === 0 && (
        <p className={`text-sm ${tc.textSecondary}`} data-testid="hours-approval-empty">
          {t(ownOnly && status === "pending" ? "payroll.hoursApprovalPage.emptyOwn" : status === "pending" ? "payroll.hoursApproval.empty" : "payroll.hoursApproval.emptyDecided")}
        </p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left text-xs ${tc.textSecondary}`}>
                <th className="py-1 pr-2">{t("payroll.hoursApprovalPage.cols.day")}</th>
                <th className="py-1 pr-2">{t("payroll.hoursApproval.cols.person")}</th>
                <th className="py-1 pr-2" title={t("payroll.hoursApprovalPage.origin.hint")}>
                  {t("payroll.hoursApprovalPage.origin.col")}
                </th>
                <th className="py-1 pr-2">{t("payroll.hoursApproval.cols.project")}</th>
                <th className="py-1 pr-2">{t("payroll.hoursApprovalPage.cols.kind")}</th>
                <th className="py-1 pr-2">{t("payroll.hoursApprovalPage.cols.note")}</th>
                <th className="py-1 pr-2 text-right">{t("payroll.hoursApprovalPage.cols.time")}</th>
                {canSeeAmounts && <th className="py-1 pr-2 text-right">{t("payroll.hoursApproval.cols.rate")}</th>}
                {canSeeAmounts &&
                  AMOUNT_CURRENCIES.map((c) => (
                    <th key={c} className="py-1 pr-2 text-right" title={t("payroll.hoursApproval.amountHint")}>
                      {t("payroll.hoursApproval.cols.amountIn", { currency: c })}
                    </th>
                  ))}
                <th className="py-1 pr-2">{t("payroll.hoursApprovalPage.cols.requestedAt")}</th>
                <th className="py-1 pr-2">{t("payroll.hoursApprovalPage.cols.status")}</th>
                {status === "pending" && !ownOnly && <th className="py-1 pr-2">{t("payroll.hoursApproval.cols.actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`border-t ${tc.cardBorder} ${tc.text}`} data-testid={`hours-approval-row-${r.id}`}>
                  <td className="py-1.5 pr-2 whitespace-nowrap">{r.date}</td>
                  <td className="py-1.5 pr-2 whitespace-nowrap font-medium">{r.userName}</td>
                  {/* #1357: de dónde salió la solicitud y qué la respalda. Una entrada del
                      skill con commits no pasa por aquí; la que llega es porque NO cita
                      evidencia (se dictó o salió de archivos sin commit): se dice. */}
                  <td className="py-1.5 pr-2" data-testid={`hours-approval-origin-${r.id}`}>
                    <span className={`whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium ${ORIGIN_TONES[r.source] ?? ORIGIN_TONES.other}`}>{sourceLabel(r.source)}</span>
                    {(() => {
                      const ev = evidenceCount(r.evidence);
                      return ev.total > 0 ? (
                        <span className={`mt-0.5 block text-[11px] ${tc.textSecondary}`}>
                          {(["commits", "prs", "issues", "artifacts"] as const)
                            .filter((k) => ev[k] > 0)
                            .map((k) => t(`payroll.hoursApprovalPage.origin.${k}`, { count: ev[k] }))
                            .join(" · ")}
                        </span>
                      ) : (
                        <span className="mt-0.5 block text-[11px] font-medium text-amber-600" title={t(`payroll.hoursApprovalPage.origin.whyHint.${r.source}`, { defaultValue: t("payroll.hoursApprovalPage.origin.whyHint.other") })}>
                          {t(`payroll.hoursApprovalPage.origin.noEvidence.${r.source}`, { defaultValue: t("payroll.hoursApprovalPage.origin.noEvidence.other") })}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="py-1.5 pr-2">
                    <span className="whitespace-nowrap">{r.projectLabel}</span>
                    {r.repo && <span className={`block font-mono text-[11px] ${tc.textSecondary}`}>{r.repo}</span>}
                  </td>
                  <td className="py-1.5 pr-2 whitespace-nowrap" data-testid={`hours-approval-kind-${r.id}`}>
                    <span className={`rounded-full bg-gray-400/15 px-1.5 py-0.5 text-[11px] ${tc.textSecondary}`}>{t(`workHours.timeEntries.kinds.${r.kind}`, { defaultValue: r.kind })}</span>
                  </td>
                  <td className="py-1.5 pr-2">
                    {r.note}
                    {/* #1303: ticket ligado a la entrada (clave + título), con enlace al tablero. */}
                    <span className="block">
                      <ActivityLink activityId={r.activityId} activityKey={r.activityKey} activityTitle={r.activityTitle} secondaryClass={tc.textSecondary} variant="inline" testId={`hours-approval-activity-${r.id}`} />
                    </span>
                    {r.flags.length > 0 && <span className="block text-[11px] text-orange-600">{r.flags.map((f) => f.message).join(" · ")}</span>}
                  </td>
                  <td className="py-1.5 pr-2 text-right font-semibold whitespace-nowrap" data-testid={`hours-approval-hours-${r.id}`}>
                    {formatMinutes(r.minutes)} h
                    {r.start && r.end && <span className={`block text-[11px] font-normal ${tc.textSecondary}`}>{r.start}–{r.end}</span>}
                  </td>
                  {canSeeAmounts && (
                    <td className={`py-1.5 pr-2 text-right whitespace-nowrap ${tc.textSecondary} ${MONEY_PRIVACY_CLASS}`}>
                      {r.rate ? formatAmount(r.rate.hourlyCost, r.rate.currency) : "—"}
                    </td>
                  )}
                  {/* El importe en pesos y en dólares: en negrita el de la moneda en que
                      cobra la persona; el otro (≈) es su equivalente al tipo de cambio. */}
                  {canSeeAmounts &&
                    AMOUNT_CURRENCIES.map((c) => {
                      const a = amountIn(r.amount, c);
                      return (
                        <td
                          key={c}
                          className={`py-1.5 pr-2 text-right whitespace-nowrap ${a && !a.converted ? "font-semibold" : tc.textSecondary} ${MONEY_PRIVACY_CLASS}`}
                          data-testid={`hours-approval-amount-${c.toLowerCase()}-${r.id}`}
                          title={a?.converted ? t("payroll.hoursApproval.amountConverted", { currency: r.amount?.currency }) : undefined}
                        >
                          {a ? `${a.converted ? "≈ " : ""}${formatAmount(a.value, c)}` : "—"}
                          {/* Quien cobra en una tercera moneda: su importe real no cabe en
                              ninguna de las dos columnas; se dice bajo la primera. */}
                          {c === AMOUNT_CURRENCIES[0] && r.amount && !(AMOUNT_CURRENCIES as readonly string[]).includes(r.amount.currency) && (
                            <span className={`block text-[11px] font-semibold ${tc.text}`} data-testid={`hours-approval-amount-paid-${r.id}`}>
                              {t("payroll.hoursApproval.amountPaidIn", { amount: formatAmount(r.amount.value, r.amount.currency) })}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  <td className={`py-1.5 pr-2 whitespace-nowrap text-xs ${tc.textSecondary}`}>
                    {new Date(r.createdAt).toLocaleString(language.startsWith("en") ? "en-US" : "es-MX", { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className="py-1.5 pr-2" data-testid={`hours-approval-status-${r.id}`}>
                    <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${STATUS_TONES[r.approval.status]}`}>{t(`payroll.hoursApprovalPage.statusOne.${r.approval.status}`)}</span>
                    {r.approval.status === "pending" ? (
                      lacksApprover(r) ? (
                        <span className="mt-0.5 block text-[11px] font-medium text-amber-600" data-testid={`hours-approval-no-approver-${r.id}`}>
                          {t("payroll.hoursApprovalPage.noApprover")}
                        </span>
                      ) : (
                        ownOnly && <span className={`block text-[11px] ${tc.textSecondary}`}>{t("payroll.hoursApprovalPage.waiting")}</span>
                      )
                    ) : (
                      <span className={`mt-0.5 block text-[11px] ${r.approval.status === "rejected" ? "text-red-500" : "text-emerald-600"}`}>
                        {t("payroll.hoursApproval.decidedBy", { name: r.approval.decidedByName || r.approval.decidedBy || "—", date: dateLabel(r.approval.decidedAt) })}
                        {r.approval.status === "rejected" && r.approval.reason && (
                          <span className={`block italic ${tc.textSecondary}`}>{t("payroll.hoursApproval.reason", { reason: r.approval.reason })}</span>
                        )}
                      </span>
                    )}
                  </td>
                  {status === "pending" && !ownOnly && (
                    <td className="py-1.5 pr-2 whitespace-nowrap">
                      {r.canDecide ? (
                        <span className="flex gap-1">
                          <button
                            type="button"
                            title={t("payroll.hoursApproval.approve")}
                            aria-label={`approve-hours-${r.id}`}
                            disabled={busy === r.id}
                            onClick={() => void decide(r, "approved")}
                            className="rounded-md border border-emerald-500/40 px-2 py-1 text-[11px] font-medium text-emerald-600 hover:bg-emerald-500/10 disabled:opacity-60"
                          >
                            <i className="ri-check-line mr-1" />
                            {t("payroll.hoursApproval.approve")}
                          </button>
                          <button
                            type="button"
                            title={t("payroll.hoursApproval.reject")}
                            aria-label={`reject-hours-${r.id}`}
                            disabled={busy === r.id}
                            onClick={() => void decide(r, "rejected")}
                            className="rounded-md border border-red-500/40 px-2 py-1 text-[11px] font-medium text-red-500 hover:bg-red-500/10 disabled:opacity-60"
                          >
                            <i className="ri-close-line mr-1" />
                            {t("payroll.hoursApproval.reject")}
                          </button>
                        </span>
                      ) : (
                        // El servidor lista las propias sin botón: nadie aprueba lo suyo,
                        // tampoco un dueño ni un tech lead (#1346).
                        <span className={`text-[11px] ${tc.textSecondary}`} data-testid={`hours-approval-own-${r.id}`}>
                          {t("payroll.hoursApproval.own")}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
